import events from 'events/events.js'
import { create } from 'superstruct'
import pDefer from 'p-defer'
import fiatClient from './client.js'
import { ORDER_EVENT_TYPES, ORDER_TYPES, PAYMENT_METHOD_TYPES, PROVIDERS } from './constants.js'
import {
  ERROR_ASSETS_ALL_PROVIDERS_FAILED,
  ERROR_COUNTRIES_ALL_PROVIDERS_FAILED,
  ERROR_QUOTES_ALL_PROVIDERS_FAILED,
  AggregateError,
} from './errors.js'
import { Provider } from './schema/provider.js'
import { omitUndefinedValues } from './utils.js'

export * from './utils.js'
export * from './constants.js'
export * from './errors.js'

const { EventEmitter } = events

export class Fiat extends EventEmitter {
  #fetch
  #authenticated = pDefer()
  #appId

  constructor({
    appId = 'exodus',
    countries = new Map(),
    geolocation,
    fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
    metadata,
    providers = [],
    serverUrl,
  } = {}) {
    super()

    this.assets = new Map()
    this.#appId = appId
    this.countries = countries
    this.geolocation = geolocation
    this.metadata = metadata
    this.serverUrl = serverUrl
    this.providers = new Map(
      providers.map((provider) => [provider.name, create(provider, Provider)])
    )
    this.#fetch = globalFetch
  }

  #getAvailableProviders = async ({
    countryCode,
    fromAsset,
    paymentMethod,
    toAsset,
    type,
  } = {}) => {
    const isBuy = type === ORDER_TYPES.buy
    const supportedProperty = isBuy ? 'isBuySupported' : 'isSellSupported'
    const providerMap = new Map()
    const getProviderHasAssets = (provider) => {
      if (fromAsset && toAsset) {
        return [fromAsset, toAsset]
          .map((asset) => this.assets.get(asset))
          .every((asset) => asset?.providers?.[provider])
      }

      if (this.assets.size === 0) {
        return true
      }

      return [...this.assets.values()].some((asset) => asset?.providers?.[provider])
    }

    for (const [name, provider] of this.providers) {
      const isEnabledFunction = typeof provider.enabled === 'function'
      const isEnabled = isEnabledFunction ? await provider.enabled({ type }) : provider.enabled

      if (!isEnabled || (type && !provider.orderTypes.includes(type))) {
        continue
      }

      const hasAssets = getProviderHasAssets(name)
      const providerInCountry = this.countries.get(countryCode)?.providers?.[name]
      const hasCountry =
        !countryCode || (type ? providerInCountry?.[supportedProperty] : providerInCountry)

      const hasPaymentMethod =
        !paymentMethod ||
        provider.getPaymentMethod(paymentMethod, {
          appId: this.#appId,
          country: countryCode,
          currency: isBuy ? fromAsset : toAsset,
          cryptoAsset: isBuy ? toAsset : fromAsset,
          orderType: type,
        })

      if (hasAssets && hasCountry && hasPaymentMethod) {
        providerMap.set(name, provider)
      }
    }

    return providerMap
  }

  setOptions({
    geolocation = this.geolocation,
    metadata = this.metadata,
    providers,
    serverUrl = this.serverUrl,
  } = {}) {
    this.geolocation = geolocation
    this.metadata = metadata
    this.serverUrl = serverUrl
    this.providers = Array.isArray(providers)
      ? new Map(providers.map((provider) => [provider.name, create(provider, Provider)]))
      : this.providers
  }

  async authenticate(keyPair) {
    if (!this.serverUrl) {
      throw new Error('`serverUrl` is required')
    }

    const metadata = typeof this.metadata === 'function' ? await this.metadata() : this.metadata

    this.client = fiatClient({
      fetch: this.#fetch,
      keyPair,
      metadata,
      url: this.serverUrl,
    })

    this.#authenticated.resolve()
  }

  async fetchAssets({ fromAsset, paymentMethod, type, useServerData } = {}) {
    if (this.countries.size === 0) {
      await this.fetchCountries({ type, useServerData })
    }

    this.assets.clear()

    const errors = []
    const { countryCode, ip, regionCode } = this.geolocation || {}
    const data = omitUndefinedValues({
      countryCode,
      fromAsset,
      regionCode,
      paymentMethod,
      type,
    })

    const providers = await this.#getAvailableProviders({ countryCode, type })

    if (useServerData) {
      try {
        const response = await this.client.getAssets({
          ...data,
          providers: [...providers.keys()],
        })

        for (const asset of response) {
          this.assets.set(asset.name, asset)
        }

        return Object.fromEntries(this.assets)
      } catch (error) {
        const error_ = new AggregateError([error], ERROR_ASSETS_ALL_PROVIDERS_FAILED.message)
        error_.code = ERROR_ASSETS_ALL_PROVIDERS_FAILED.code
        throw error_
      }
    }

    // The code below and the "useServerData" remote config will be removed once the getAssets endpoint is available in production.
    const result = await Promise.all(
      [...providers.values()].map(async (provider) =>
        Promise.allSettled([
          provider.name,
          provider.getAssets({
            ...data,
            ip,
            serverClient: this.client,
          }),
        ])
      )
    )

    for (const [{ value: provider }, { status, reason, value: assets }] of result) {
      if (status === 'rejected') {
        reason.provider = provider
        errors.push(reason)
        continue
      }

      for (const { name, type, ...providerAsset } of assets) {
        const existingAsset = this.assets.get(name)

        this.assets.set(name, {
          name,
          type,
          providers: {
            ...existingAsset?.providers,
            [provider]: providerAsset,
          },
        })
      }
    }

    if (errors.length > 0 && this.assets.size === 0) {
      const error = new AggregateError(errors, ERROR_ASSETS_ALL_PROVIDERS_FAILED.message)
      error.code = ERROR_ASSETS_ALL_PROVIDERS_FAILED.code
      throw error
    }

    return Object.fromEntries(this.assets)
  }

  async fetchCountries({ paymentMethod, type, useServerData } = {}) {
    this.countries.clear()

    const errors = []
    const { countryCode, regionCode } = this.geolocation || {}
    const data = omitUndefinedValues({
      countryCode,
      paymentMethod,
      regionCode,
    })
    const providers = await this.#getAvailableProviders({ type })

    if (useServerData) {
      try {
        const response = await this.client.getCountries({
          ...data,
          providers: [...providers.keys()],
        })

        for (const country of response) {
          this.countries.set(country.code, country)
        }

        return Object.fromEntries(this.countries)
      } catch (error) {
        const error_ = new AggregateError([error], ERROR_COUNTRIES_ALL_PROVIDERS_FAILED.message)
        error_.code = ERROR_COUNTRIES_ALL_PROVIDERS_FAILED.code
        throw error_
      }
    }

    const result = await Promise.all(
      [...providers.values()].map(async (provider) =>
        Promise.allSettled([
          provider.name,
          provider.getCountries({
            ...data,
            serverClient: this.client,
          }),
        ])
      )
    )

    for (const [{ value: provider }, { status, reason, value: countries }] of result) {
      if (status === 'rejected') {
        reason.provider = provider
        errors.push(reason)
        continue
      }

      for (const { code, name, ...providerCountry } of countries) {
        const existingCountry = this.countries.get(code)

        this.countries.set(code, {
          code,
          name,
          providers: {
            ...existingCountry?.providers,
            [provider]: providerCountry,
          },
        })
      }
    }

    if (errors.length > 0 && this.countries.size === 0) {
      const error = new AggregateError(errors, ERROR_COUNTRIES_ALL_PROVIDERS_FAILED.message)
      error.code = ERROR_COUNTRIES_ALL_PROVIDERS_FAILED.code
      throw error
    }

    return Object.fromEntries(this.countries)
  }

  getPaymentMethods({ cryptoAsset, fiatCurrency, orderType, provider } = Object.create(null)) {
    const { countryCode } = this.geolocation ?? {}
    const country = this.countries.get(countryCode)
    const existingCryptoAsset = this.assets.get(cryptoAsset)
    const existingFiatAsset = this.assets.get(fiatCurrency)
    const paymentMethods = new Map()
    const supportedProperty = orderType === ORDER_TYPES.buy ? 'isBuySupported' : 'isSellSupported'

    for (const [provider_, providerInstance] of this.providers) {
      const isMetaProvider = providerInstance.providers?.includes(provider)
      const hasProviderCrypto =
        !existingCryptoAsset || existingCryptoAsset.providers[provider_]?.[supportedProperty]

      const hasProviderFiat =
        !existingFiatAsset || existingFiatAsset.providers[provider_]?.[supportedProperty]

      const providerInCountry = country?.providers?.[provider_]
      const hasProviderCountry =
        !country || (orderType ? providerInCountry?.[supportedProperty] : providerInCountry)

      if (!hasProviderCrypto || !hasProviderFiat || !hasProviderCountry) {
        continue
      }

      for (const paymentMethodType of Object.values(PAYMENT_METHOD_TYPES)) {
        const providerPaymentMethods = new Map()
        const existingPaymentMethods = paymentMethods.get(paymentMethodType)
        const providers = isMetaProvider ? providerInstance.providers : [provider_]

        for (const provider of providers) {
          const paymentMethod = providerInstance.getPaymentMethod(paymentMethodType, {
            appId: this.#appId,
            country: countryCode,
            cryptoAsset,
            currency: fiatCurrency,
            orderType,
            ...(isMetaProvider ? { provider } : {}),
          })

          if (paymentMethod) {
            providerPaymentMethods.set(provider, paymentMethod)
          }
        }

        if (providerPaymentMethods.size === 0) {
          continue
        }

        paymentMethods.set(paymentMethodType, {
          ...existingPaymentMethods,
          ...Object.fromEntries(providerPaymentMethods),
        })
      }
    }

    return Object.fromEntries(paymentMethods)
  }

  async getQuote(provider, { fromAmount, toAmount, type = ORDER_TYPES.buy, ...parameters } = {}) {
    if (!this.providers.has(provider)) {
      throw new Error(`Provider "${provider}" does not exist`)
    }

    if (fromAmount && toAmount) {
      throw new Error('`fromAmount` and `toAmount` are mutually exclusive')
    }

    return this.providers
      .get(provider)
      .getQuote?.({ fromAmount, toAmount, type, ...parameters, serverClient: this.client })
  }

  async getQuotes({ fromAsset, paymentMethod, toAsset, type, useServerData, ...parameters } = {}) {
    if (this.assets.size === 0) {
      await this.fetchAssets({ fromAsset, paymentMethod, type, useServerData })
    }

    const errors = []
    const quotesMap = new Map()
    const providers = await this.#getAvailableProviders({
      countryCode: this.geolocation?.countryCode,
      fromAsset,
      paymentMethod,
      toAsset,
      type,
    })

    const data = omitUndefinedValues({
      ...parameters,
      appId: this.#appId,
      countryCode: this.geolocation?.countryCode,
      fromAsset,
      paymentMethod,
      toAsset,
      type,
    })

    if (useServerData) {
      try {
        const response = await this.client.getQuotes({
          ...data,
          providers: [...providers.keys()],
        })

        const quotes = providers.size > 1 ? Object.values(response) : [response]

        for (const { provider, ...quote } of quotes) {
          const hasNativeProviderClash = providers.get(provider).providers && PROVIDERS[provider]
          if (hasNativeProviderClash) {
            continue
          }

          quotesMap.set(provider, { ...quote, provider })
        }

        return Object.fromEntries(quotesMap)
      } catch (error) {
        const error_ = new AggregateError([error], ERROR_QUOTES_ALL_PROVIDERS_FAILED.message)
        error_.code = ERROR_QUOTES_ALL_PROVIDERS_FAILED.code
        throw error
      }
    }

    const result = await Promise.all(
      [...providers.values()].map(async (provider) =>
        Promise.allSettled([
          { name: provider.name, providers: provider.providers },
          this.getQuote(provider.name, {
            ...data,
            paymentMethod,
            type,
          }),
        ])
      )
    )

    for (const [{ value: provider }, { status, reason, value }] of result) {
      if (status === 'rejected') {
        reason.provider = provider.name
        errors.push(reason)
        continue
      }

      const quotes = Array.isArray(value) ? value : [value]

      for (const quote of quotes) {
        const hasNativeProviderClash = provider.providers && PROVIDERS[quote.provider]
        if (hasNativeProviderClash) {
          continue
        }

        quotesMap.set(quote.provider, quote)
      }
    }

    if (errors.length > 0 && quotesMap.size === 0) {
      const error = new AggregateError(errors, ERROR_QUOTES_ALL_PROVIDERS_FAILED.message)
      error.code = ERROR_QUOTES_ALL_PROVIDERS_FAILED.code
      throw error
    }

    return Object.fromEntries(quotesMap)
  }

  async getOrder(
    id,
    { providerId, providerToken, provider, type, useServerData, ...order } = Object.create(null)
  ) {
    await this.#authenticated.promise

    if (useServerData) {
      return this.client.getOrder(id)
    }

    if (!providerId || !provider) {
      order = await this.client.getOrder(id)
      providerId = order.providerId
      providerToken = order.providerToken
      provider = order.provider
      type = order.type
    }

    const isDraft = !providerId

    if (isDraft) {
      return { ...order, id, provider, providerId, providerToken, type }
    }

    const providerInstance = [...this.providers.values()].find(
      (instance) => instance.name === provider || instance.providers?.includes(provider)
    )

    if (!providerInstance) {
      throw new Error(`Provider "${provider}" does not exist`)
    }

    const providerOrder = await providerInstance.getOrder(providerId, {
      serverClient: this.client,
      serverOrder: order,
      token: providerToken,
      type,
    })

    return {
      ...order,
      ...omitUndefinedValues(providerOrder),
      id,
      providerToken,
      type,
    }
  }

  async getOrders() {
    return this.client.getOrders()
  }

  async createOrder(parameters = {}) {
    return this.client.createOrder({
      ...parameters,
      country: this.geolocation?.countryCode,
    })
  }

  async updateOrder(...parameters) {
    return this.client.updateOrder(...parameters)
  }

  async updateUser(...parameters) {
    return this.client.updateUser(...parameters)
  }

  async addOrderEvent({ type = ORDER_EVENT_TYPES.info, ...parameters } = {}) {
    if (!ORDER_EVENT_TYPES[type]) {
      throw new Error('Invalid type')
    }

    return this.client.addOrderEvent({ type, ...parameters })
  }

  async createOrderDeposit(id) {
    return this.client.createOrderDeposit(id)
  }

  async sign(parameters = Object.create(null)) {
    const { data } = await this.client.sign(parameters)
    return data
  }

  async signUrl(parameters = {}) {
    const { signedUrl } = await this.client.signUrl(parameters)
    return signedUrl
  }

  async getUser() {
    return this.client.getUser()
  }

  // TODO: Remove in next major version in favor of `.getUser()`
  async hasUserPassedKYC(parameters = {}) {
    return this.client.hasUserPassedKYC(parameters)
  }
}

const createFiat = (arguments_) => new Fiat(arguments_)

export default createFiat
